// =========================================================================================@@
// Last Updated Date: May 18, 2025
// Last Updated By: Steven
// Status Level: 2
// ===========================================================================================

import React, { useEffect, useRef, useState } from 'react'
import { Form, Modal, Text, View } from 'oio-react'
import pluralize from 'pluralize'
import PropTypes from 'prop-types'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom'
import { arrayMove } from 'react-sortable-hoc'
import scrollToWithAnimation from 'scrollto-with-animation'
import CloseIcon from 'assets/icons/close'
import FormIcon from 'assets/icons/form'
import RichText from 'src/sites/kits/Utils/RichText'
import { Button } from 'src/sites/kits/UI2'
import FormBuilderActionsMenu from './ActionsMenu'
import FormBuilderCardContainer from './CardContainer'
import FormBuilderPreview from './Preview'

const FormBuilder = ({
   addButtonName,
   appPrimaryColor,
   elementTypes,
   elements: initialElements,
   formBuilderExtraActions,
   formBuilderSubtitle,
   formBuilderTitle,
   formSubtitle,
   formTitle,
   onChange,
   onCloseButtonClick,
   previewComponent: PreviewComponent
}) => {
   const history = useHistory()
   const match = useRouteMatch()
   const view = useRef()

   const [elements, setElements] = useState(initialElements)
   const [actionsPopoverIsOpen, setActionsPopoverIsOpen] = useState(false)
   const [previewIsVisible, setPreviewIsVisible] = useState(true)
   const [updating, setUpdating] = useState(false)

   useEffect(() => {
      setElements(initialElements)
   }, [initialElements])

   const handleCardAdd = (options) => {
      setElements(currentElementsState => ([
         ...currentElementsState,
         options
      ]))

      scrollToWithAnimation(
         view.current.scrollContainer,
         'scrollTop',
         view.current.scrollContainer.scrollHeight,
         500
      )
   }

   const handleCardInputChange = (elementIndex, inputName, event) => {
      const eventTargetValue = event.target.value
      setElements(currentElementsState => currentElementsState.map((element, i) => (
         i === elementIndex
            ? { ...element, [inputName]: eventTargetValue }
            : element
      )))
   }

   const handleCardOptionAdd = (elementIndex) => {
      setElements(currentElementsState => currentElementsState.map((element, i) => (
         i === elementIndex
            ? { ...element, options: [...element.options, ''] }
            : element
      )))
   }

   const handleCardOptionChange = (targetElementIndex, targetOptionIndex, event) => {
      const eventTargetValue = event.target.value
      setElements(currentElementsState => currentElementsState.map((element, elementIndex) => (
         elementIndex === targetElementIndex
            ? {
               ...element,
               options: element.options.map((option, optionIdex) => (
                  optionIdex === targetOptionIndex
                     ? eventTargetValue
                     : option
               ))
            }
            : element
      )))
   }

   const handleCardOptionRemove = (elementIndex, optionIndex) => {
      setElements(currentElementsState => currentElementsState.map((element, i) => (
         i === elementIndex
            ? {
               ...element,
               options: [
                  ...element.options.slice(0, optionIndex),
                  ...element.options.slice(optionIndex + 1)
               ]
            }
            : element
      )))
   }

   const handleCardOptionSortEnd = ({ oldIndex, newIndex, collection }) => {
      setElements(currentElementsState => currentElementsState.map((element, index) => (
         index === collection
            ? { ...element, options: arrayMove(element.options.slice(), oldIndex, newIndex) }
            : element
      )))
   }

   const handleCardRemove = (elementIndex) => {
      setElements(currentElementsState => ([
         ...currentElementsState.slice(0, elementIndex),
         ...currentElementsState.slice(elementIndex + 1)
      ]))
   }

   const handleCardRequiredChange = (elementIndex, event) => {
      const isChecked = event.target.checked
      setElements(currentElementsState => currentElementsState.map((element, i) => (
         i === elementIndex
            ? { ...element, required: isChecked }
            : element
      )))
   }

   const handlePropChange = (elementIndex, propertyName, propertyValue) => {
      setElements(currentElementsState => currentElementsState.map((element, i) => (
         i === elementIndex
            ? { ...element, [propertyName]: propertyValue }
            : element
      )))
   }

   const handleCardSortEnd = ({ oldIndex, newIndex }) => {
      setElements(currentElementsState => (
         arrayMove(currentElementsState.slice(), oldIndex, newIndex)
      ))
   }

   const setAllowedFileTypes = (elementIndex, fileType, checked) => {
      if (fileType === '*/*' && checked) {
         return handlePropChange(elementIndex, 'formats', ['*/*'])
      }

      const allowedFileTypes = elements[elementIndex].formats ?? ['*/*']
      const newAllowedFileTypes = [
         ...allowedFileTypes.filter(v => v !== fileType && v !== '*/*'),
         checked
            ? fileType
            : null
      ].filter(v => !!v)

      if (newAllowedFileTypes.length === 0) {
         newAllowedFileTypes.push('*/*')
      }

      handlePropChange(elementIndex, 'formats', newAllowedFileTypes)
   }

   const handleSubmit = async () => {
      // TODO: Kinda dumb that `updating` state relies on `onChange` waiting
      // for a promise to resolve
      setUpdating(true)
      await onChange(elements)
      setUpdating(false)
   }

   return (
      <View width="100%" height="100%" style={{ overflow: 'hidden' }}>
         <div className="float-left w-[300px] h-full p-8 border-r border-solid border-neutral-200">
            <View
               display="flex"
               justifyContent="center"
               alignItems="center"
               width="36px"
               height="36px"
               borderRadius="50%"
               backgroundColor="#000">
               <FormIcon
                  width="20px"
                  height="20px"
                  strokeWidth="2px"
                  color="#fff"
               />
            </View>
            <div className="text-2xl lg:text-xl tracking-tight font-semibold mt-4">
               {formTitle}
            </div>
            <div className="relative -top-1 text-xl lg:text-lg tracking-tight opacity-60">
               {formBuilderTitle}
            </div>
            <div className="max-w-3xl text-sm text-neutral-500 text-balance mt-4 mb-6">
               <RichText float="none" html={formBuilderSubtitle} />
            </div>
            <div className="w-full flex flex-col gap-2">
               {formBuilderExtraActions}
               <Button
                  mode={updating ? 'loading' : 'normal'}
                  onClick={handleSubmit}
                  size="md"
                  className="w-full bg-black text-white hover:bg-neutral-800">
                  Save
               </Button>
               <DropdownMenu.Root
                  open={actionsPopoverIsOpen}
                  onOpenChange={open => setActionsPopoverIsOpen(open)}>
                  <DropdownMenu.Trigger>
                     <Button
                        size="md"
                        className="w-full bg-neutral-100 text-neutral-500 hover:bg-neutral-200">
                        {addButtonName}
                     </Button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                     <DropdownMenu.Content
                        className="ui-popover"
                        collisionPadding={16}>
                        <FormBuilderActionsMenu
                           elementTypes={elementTypes}
                           onButtonClick={(elementName) => {
                              handleCardAdd(elementName)
                              setActionsPopoverIsOpen(false)
                           }}
                        />
                     </DropdownMenu.Content>
                  </DropdownMenu.Portal>
               </DropdownMenu.Root>
               {PreviewComponent && (
                  <Button
                     linkTo={`${match.url}/preview`}
                     width="100%"
                     icon="ion-eye"
                     size="md"
                     className="bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
                     style={{ position: 'static' }}>
                     Preview Form
                  </Button>
               )}
            </div>
         </div>
         <div className="float-left w-[calc(100%-300px)] h-full bg-neutral-50">
            <View
               display="flex"
               justifyContent="space-between"
               alignItems="center"
               float="left"
               width="100%"
               height="60px"
               padding="0px 20px 0 24px">
               <div className="text-xs text-neutral-500 font-medium">
                  {`This form currently has ${elements.length} ${pluralize('element', elements.length)}`}
               </div>
               <View
                  width="30px"
                  flex="0 0 auto"
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  onClick={onCloseButtonClick}>
                  <CloseIcon strokeWidth="2" width="16px" height="16px" />
               </View>
            </View>
            <View height="calc(100% - 60px)" width="100%">
               {elements.length > 0 && (
                  <Form
                     elementAppearance="plain"
                     elementBackgroundColor="#eee"
                     elementFocusBackgroundColor="rgba(250, 147, 104, 0.1)"
                     style={{ width: '100%', height: '100%' }}>
                     <FormBuilderCardContainer
                        ref={view}
                        elements={elements}
                        onInputChange={handleCardInputChange}
                        onOptionAdd={handleCardOptionAdd}
                        onOptionChange={handleCardOptionChange}
                        onOptionRemove={handleCardOptionRemove}
                        onOptionSortEnd={handleCardOptionSortEnd}
                        onRequiredChange={handleCardRequiredChange}
                        onPropChange={handlePropChange}
                        onRemove={handleCardRemove}
                        onSortEnd={handleCardSortEnd}
                        setAllowedFileTypes={setAllowedFileTypes}
                        useDragHandle
                     />
                  </Form>
               )}
               {!elements.length && (
                  <View width="100%" padding="60px 120px" textAlign="center">
                     <Text size="2" color="#333">
                        Your form currently has no fields, please click the
                        <b>{` ${addButtonName} `}</b>
                        button to get started
                     </Text>
                  </View>
               )}
            </View>
         </div>
         {PreviewComponent && (
            <Switch>
               <Route
                  path={`${match.url}/preview`}
                  render={() => (
                     <Modal
                        borderRadius="6px"
                        overlayBackgroundColor="rgba(20,20,20,0.9)"
                        width="100%[a-b] 90%[c] 540px[d-f]"
                        height="100%[a-b] 98%[c-f]"
                        onCloseComplete={() => {
                           history.push(match.url)
                           setPreviewIsVisible(true)
                        }}
                        onCloseTrigger={() => setPreviewIsVisible(false)}
                        open={previewIsVisible}
                        zIndex="var(--settingsModalLevel3ZIndex)">
                        <PreviewComponent
                           elements={elements}
                           onCloseButtonClick={() => setPreviewIsVisible(false)}
                           subtitle={formSubtitle}
                           title={formTitle}
                        />
                     </Modal>
                  )}
               />
            </Switch>
         )}
      </View>
   )
}

FormBuilder.propTypes = {
   addButtonName: PropTypes.string,
   appPrimaryColor: PropTypes.string,
   elementTypes: PropTypes.array,
   elements: PropTypes.array,
   formBuilderExtraActions: PropTypes.node,
   formBuilderSubtitle: PropTypes.string,
   formBuilderTitle: PropTypes.string,
   formSubtitle: PropTypes.string.isRequired,
   formTitle: PropTypes.string.isRequired,
   onChange: PropTypes.func.isRequired,
   onCloseButtonClick: PropTypes.func.isRequired,
   previewComponent: PropTypes.oneOfType([
      PropTypes.instanceOf(React.Component),
      PropTypes.func
   ])
}

FormBuilder.defaultProps = {
   addButtonName: 'Add Element',
   appPrimaryColor: '#d14c2f',
   elementTypes: ['info', 'text', 'paragraph', 'radio', 'checkbox', 'file', 'dropdown'],
   elements: [],
   formBuilderExtraActions: null,
   formBuilderSubtitle: 'This app will help you build a form',
   formBuilderTitle: 'Form Builder',
   previewComponent: FormBuilderPreview
}

export default FormBuilder
